/** @typedef {{ canInstall: boolean, isInstalled: boolean, showIosHint: boolean }} PwaInstallState */

/** @type {BeforeInstallPromptEvent | null} */
let deferredPrompt = null;

/** @type {Set<(state: PwaInstallState) => void>} */
const listeners = new Set();

function isPwaInstalled() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    window.navigator.standalone === true
  );
}

function isIosDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function getPwaInstallState() {
  const installed = isPwaInstalled();
  const canInstall = Boolean(deferredPrompt) && !installed;
  const showIosHint =
    !installed &&
    !deferredPrompt &&
    isIosDevice() &&
    "serviceWorker" in navigator;

  return {
    canInstall,
    isInstalled: installed,
    showIosHint,
  };
}

function notifyListeners() {
  const state = getPwaInstallState();
  listeners.forEach((listener) => listener(state));
}

export function subscribePwaInstall(listener) {
  listeners.add(listener);
  listener(getPwaInstallState());
  return () => listeners.delete(listener);
}

export function initPwaInstall() {
  if (typeof window === "undefined") {
    return;
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notifyListeners();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notifyListeners();
  });
}

export async function promptPwaInstall() {
  if (!deferredPrompt) {
    return "unavailable";
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;

  if (outcome === "accepted") {
    deferredPrompt = null;
    notifyListeners();
    return "accepted";
  }

  return "dismissed";
}

export function isPwaInstallVisible(state = getPwaInstallState()) {
  return state.canInstall || state.showIosHint;
}
